import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, defer, finalize, map, Observable, of, shareReplay, tap, throwError } from 'rxjs';
import { GlobalDataService } from '../global-data/global-data.service';
import { MainService } from '../main.service';

import { ApplicationRole, EffectiveAccess, hasRole, noAccess } from '../../guards/auth/roles';

export type AdministrationRole = ApplicationRole;
export interface RoleAssignmentUpdate {
  roles?: AdministrationRole[];
  editorStoreIds?: number[];
  expectedRoles?: string[];
  expectedEditorStoreIds?: number[];
}
export interface EditorStoreOption {
  id: number;
  place: string | null;
  isActive: boolean;
}
export interface AdministrationUser {
  id: number;
  userId: string;
  displayName: string | null;
  /** Application scoped SAM administration roles that the UI may edit. */
  roles: string[];
  editorStoreIds: number[];
  userType: string;
  /** Unsupported assignments are displayed for context. */
  otherAssignments?: { role: string; scopeType: string; scope: unknown }[];
}

@Injectable({ providedIn: 'root' })
export class AdministrationService {
  private rolesRequestGeneration = 0;
  private pendingRolesRequest?: {
    key: string;
    request: Observable<EffectiveAccess>;
  };

  constructor(private http: HttpClient, private main: MainService, private global: GlobalDataService) {}

  getMyRoles(): Observable<EffectiveAccess> {
    return defer(() => this.getOrStartRolesRequest(false));
  }

  refreshMyRoles(): Observable<EffectiveAccess> {
    return defer(() => this.getOrStartRolesRequest(true));
  }

  getEditorStores(): Observable<EditorStoreOption[]> {
    return this.http.get<EditorStoreOption[]>(this.main.getApiURL() + 'Administration/editor-stores', {
      headers: this.main.getHeaders(),
    });
  }

  private getOrStartRolesRequest(force: boolean): Observable<EffectiveAccess> {
    const requestingSession = this.global.getSessionData().value;
    const authenticationGeneration = this.global.getAuthenticationGeneration();
    const key = JSON.stringify([
      requestingSession.initialized,
      requestingSession.userId,
      requestingSession.sessionVersion,
      authenticationGeneration,
    ]);

    if (!force && this.pendingRolesRequest?.key === key) {
      return this.pendingRolesRequest.request;
    }

    const requestGeneration = ++this.rolesRequestGeneration;
    let pendingRequest!: Observable<EffectiveAccess>;
    const request = this.http.get<EffectiveAccess>(this.main.getApiURL() + 'Administration/me', {
      headers: this.main.getHeaders(),
    }).pipe(
      catchError(error => {
        return this.isCurrentRolesRequest(requestGeneration, authenticationGeneration, requestingSession)
          ? throwError(() => error)
          : of(noAccess);
      }),
      map(response => this.isCurrentRolesRequest(requestGeneration, authenticationGeneration, requestingSession)
        ? response
        : noAccess),
      tap(access => {
        if (this.isCurrentRolesRequest(requestGeneration, authenticationGeneration, requestingSession)) {
          const session = this.global.getSessionData().value;
          const impersonating = session.storeImpersonation && hasRole(access, 'SendoutManager');
          const store = session.storeImpersonation && !impersonating ? null : session.store;
          if (store !== session.store || impersonating !== session.storeImpersonation ||
              session.matchedStoreId !== access.matchedStoreId ||
              JSON.stringify(session.roles) !== JSON.stringify(access.roles) ||
              JSON.stringify(session.editorStoreIds) !== JSON.stringify(access.editorStoreIds)) {
            this.global.setSessionData({ ...session, ...access, store, storeImpersonation: impersonating });
          }
        }
      }),
      finalize(() => {
        if (this.pendingRolesRequest?.request === pendingRequest) {
          this.pendingRolesRequest = undefined;
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    pendingRequest = request;
    this.pendingRolesRequest = { key, request };
    return request;
  }

  private isCurrentRolesRequest(
    requestGeneration: number,
    authenticationGeneration: number,
    requestingSession: { initialized: boolean; userId: number; sessionVersion?: string }
  ): boolean {
    const session = this.global.getSessionData().value;
    return requestGeneration === this.rolesRequestGeneration &&
      authenticationGeneration === this.global.getAuthenticationGeneration() &&
      session.initialized &&
      session.userId === requestingSession.userId &&
      session.sessionVersion === requestingSession.sessionVersion;
  }

  getUsers(): Observable<AdministrationUser[]> {
    return this.http.get<AdministrationUser[]>(this.main.getApiURL() + 'Administration/users', { headers: this.main.getHeaders() });
  }

  updateRoles(id: number, update: RoleAssignmentUpdate): Observable<AdministrationUser> {
    return this.http.put<AdministrationUser>(this.main.getApiURL() + `Administration/users/${id}/roles`, update, { headers: this.main.getHeaders() });
  }
}
